import React from "react";
import { db } from "@/lib/firebase";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import useUserProfile from "@/hooks/useUserProfile";
import useCoopMemberRole from "@/hooks/useCoopMemberRole";
import useStoreManagement from "@/hooks/useStoreManagement";
import { assignStoreManager, removeStoreManager, STORE_TYPES } from "@/lib/storeManagement";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { User, Mail, Shield, CheckCircle, XCircle, Store, Users, Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";

export default function AdminUsers() {
  const { profile } = useUserProfile();
  const { isAdmin } = useCoopMemberRole();
  const { notify } = React.useContext(ToastContext);
  const { stores, loadAllStores } = useStoreManagement();
  
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('users');
  
  // Search, sort, and pagination state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('newest'); // 'newest' or 'oldest'
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage] = React.useState(15);
  
  // Store manager assignment state
  const [selectedStore, setSelectedStore] = React.useState('');
  const [selectedManager, setSelectedManager] = React.useState('');

  React.useEffect(() => {
    if (!isAdmin) return;

    setLoading(true);
    try {
      const unsub = onSnapshot(collection(db, "users"), (snap) => {
        const usersData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Create displayName from firstName and lastName if available
        const enrichedUsers = usersData.map(user => ({
          ...user,
          displayName: user.displayName || 
            `${user.firstName || ""} ${user.lastName || ""}`.trim() || 
            user.email || ""
        }));
        
        setUsers(enrichedUsers);
        setLoading(false);
      });
      
      return () => unsub();
    } catch (e) {
      setError(String(e));
      setLoading(false);
    }
  }, [isAdmin]);

  // Load stores when component mounts
  React.useEffect(() => {
    if (isAdmin) {
      loadAllStores();
    }
  }, [isAdmin, loadAllStores]);

  async function updateUserRole(userId, newRole) {
    try {
      await updateDoc(doc(db, "users", userId), { role: newRole || null });
      notify("User role updated successfully.", { type: "success" });
    } catch (e) {
      notify(e?.message || String(e), { type: "error" });
    }
  }



  async function assignManagerToStore() {
    if (!selectedStore || !selectedManager) {
      notify("Please select both a store and a manager.", { type: "error" });
      return;
    }

    try {
      const store = stores.find(s => s.id === selectedStore);
      if (!store) {
        notify("Store not found.", { type: "error" });
        return;
      }

      const manager = users.find(u => u.id === selectedManager);
      if (!manager) {
        notify("Manager not found.", { type: "error" });
        return;
      }

      // Determine the appropriate store manager role based on store type
      let managerRole;
      switch (store.type) {
        case STORE_TYPES.COOPERATIVE:
          managerRole = 'cooperative_store_manager';
          break;
        case STORE_TYPES.MEMBER:
          managerRole = 'member_store_manager';
          break;
        case STORE_TYPES.ESTABLISHMENT:
          managerRole = 'establishment_store_manager';
          break;
        default:
          notify("Invalid store type.", { type: "error" });
          return;
      }

      // Assign the store manager role to the user
      await updateDoc(doc(db, "users", selectedManager), { 
        role: managerRole 
      });

      // Assign the manager to the store
      const result = await assignStoreManager(selectedStore, selectedManager, managerRole);
      
      if (result.success) {
        notify(`Successfully assigned ${manager.displayName} as manager of ${store.name}.`, { type: "success" });
        setSelectedStore('');
        setSelectedManager('');
      } else {
        notify(`Failed to assign manager: ${result.error}`, { type: "error" });
      }
    } catch (e) {
      notify(e?.message || String(e), { type: "error" });
    }
  }

  async function removeManagerFromStore(storeId) {
    try {
      const result = await removeStoreManager(storeId);
      if (result.success) {
        notify("Manager removed from store successfully.", { type: "success" });
      } else {
        notify(`Failed to remove manager: ${result.error}`, { type: "error" });
      }
    } catch (e) {
      notify(e?.message || String(e), { type: "error" });
    }
  }

  // Filter, sort, and paginate users
  const filteredUsers = React.useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.displayName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      
      if (sortOrder === 'newest') {
        return bTime - aTime; // Newest first
      } else {
        return aTime - bTime; // Oldest first
      }
    });

    return filtered;
  }, [users, searchTerm, sortOrder]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder]);

  // Get users who can be store managers (not already store managers)
  const availableManagers = users.filter(user => 
    !user.role || !user.role.includes('store_manager')
  );

  // Get store type label
  const getStoreTypeLabel = (type) => {
    switch (type) {
      case STORE_TYPES.COOPERATIVE:
        return 'Cooperative Store';
      case STORE_TYPES.MEMBER:
        return 'Member Store';
      case STORE_TYPES.ESTABLISHMENT:
        return 'Establishment Store';
      default:
        return type;
    }
  };

  if (!isAdmin) return <div className="card p-4">You do not have access.</div>;

  if (loading) return <div className="card p-4">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Manage website user accounts, roles, and store managers</p>
        </div>
        <div className="text-sm text-gray-500">
          {searchTerm ? (
            <>Showing {filteredUsers.length} of {users.length} users</>
          ) : (
            <>Total Users: {users.length}</>
          )}
        </div>
      </div>

      {/* Search and Sort Controls */}
      {activeTab === 'users' && (
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Sort Dropdown */}
            <div className="sm:w-48">
              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            User Roles
          </button>
          <button
            onClick={() => setActiveTab('store-managers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'store-managers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Store className="w-4 h-4 inline mr-2" />
            Store Managers
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <>
          {/* Info Box */}
          <div className="card p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Website User Roles</p>
                <p>This page manages website user accounts and their delivery-related roles. Cooperative member roles are managed separately in the Coop Membership section.</p>
              </div>
            </div>
          </div>

          {/* Desktop Table */}
      <div className="hidden lg:block">
        <div className="card p-0">
          <div className="overflow-x-auto max-w-full">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-32">
                            {user.displayName || "No Name"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 truncate max-w-56">{user.email}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {user.role ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            ['delivery', 'rider', 'packer', 'cashier'].includes(user.role) 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        ) : (
                          <span className="text-gray-500">No role</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.suspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {user.suspended ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-2">
                        <select
                          value={user.role || ""}
                          onChange={(e) => updateUserRole(user.id, e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">No Role</option>
                          <option value="member">Member</option>
                          <option value="delivery">Delivery</option>
                          <option value="rider">Rider</option>
                          <option value="packer">Packer</option>
                          <option value="cashier">Cashier</option>
                          <option value="cooperative_store_manager">Cooperative Store Manager</option>
                          <option value="member_store_manager">Member Store Manager</option>
                          <option value="establishment_store_manager">Establishment Store Manager</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {paginatedUsers.map((user) => (
          <div key={user.id} className="card p-4">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 h-12 w-12">
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-lg font-medium text-gray-700">
                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user.displayName || "No Name"}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {user.email}
                </div>
              </div>
            </div>

            {/* Role and Status */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {user.role ? (
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  ['delivery', 'rider', 'packer', 'cashier'].includes(user.role) 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {user.role}
                </span>
              ) : (
                <span className="text-gray-500 text-sm">No role</span>
              )}
              
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                user.suspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {user.suspended ? 'Suspended' : 'Active'}
              </span>
            </div>

            {/* Actions */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Update Role</label>
              <select
                value={user.role || ""}
                onChange={(e) => updateUserRole(user.id, e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No Role</option>
                <option value="member">Member</option>
                <option value="delivery">Delivery</option>
                <option value="rider">Rider</option>
                <option value="packer">Packer</option>
                <option value="cashier">Cashier</option>
                <option value="cooperative_store_manager">Cooperative Store Manager</option>
                <option value="member_store_manager">Member Store Manager</option>
                <option value="establishment_store_manager">Establishment Store Manager</option>
              </select>
            </div>
          </div>
        ))}
      </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No users found matching your search.' : 'No users found'}
            </div>
          )}

          {/* Pagination Controls */}
          {filteredUsers.length > 0 && totalPages > 1 && (
            <div className="card p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Store Managers Tab */}
      {activeTab === 'store-managers' && (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="card p-4 bg-green-50 border-green-200">
            <div className="flex items-start gap-3">
              <Store className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">Store Manager Assignment</p>
                <p>Assign users as managers to specific stores. Store managers will have access to manage their assigned stores.</p>
              </div>
            </div>
          </div>

          {/* Store Manager Assignment Form */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Store Manager</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Store
                </label>
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a store...</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} ({getStoreTypeLabel(store.type)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Manager
                </label>
                <select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a manager...</option>
                  {availableManagers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={assignManagerToStore}
              disabled={!selectedStore || !selectedManager}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign Manager
            </button>
          </div>

          {/* Current Store Managers */}
          <div className="card p-0">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Current Store Managers</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {stores.filter(store => store.managerUid).map((store) => {
                const manager = users.find(u => u.id === store.managerUid);
                return (
                  <div key={store.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-medium text-gray-900">{store.name}</h4>
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {getStoreTypeLabel(store.type)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Manager: {manager ? manager.displayName : 'Unknown'} ({manager?.email})
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Role: {manager?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                      <button
                        onClick={() => removeManagerFromStore(store.id)}
                        className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800"
                      >
                        Remove Manager
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {stores.filter(store => store.managerUid).length === 0 && (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500">No store managers assigned yet.</p>
              </div>
            )}
          </div>

          {/* Update Products Store Info Button */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Management</h3>
            <p className="text-sm text-gray-600 mb-4">
              Product management tools have been moved to the Store Settings page.
            </p>
          </div>

        </div>
      )}
    </div>
  );
}
